import { useEffect, useRef } from 'react';
import { useAuthenticatedFetch } from './useAuthenticatedFetch';
import { captureMerchantEvent, initMerchantAnalytics } from '../analytics/client';
import type { PageName } from '../types';

/**
 * Boot merchant analytics from the settings the backend serves.
 *
 * Configuration comes from the server, not from a build-time constant, so the
 * browser key can be cleared — and client capture and replay stopped — without
 * rebuilding and redeploying the admin bundle. That is the difference between a
 * kill switch and a release.
 *
 * Fire-and-forget throughout, exactly like useEnsurePixel: analytics must never
 * block or error the admin.
 */
export function useMerchantAnalytics(): void {
  const authenticatedFetch = useAuthenticatedFetch();
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    authenticatedFetch('/api/settings/analytics')
      .then((res) => res.json().catch(() => null))
      .then((json) => {
        const data = json?.data;
        if (!data?.shop) return;
        void initMerchantAnalytics({
          key: data.browserKey ?? '',
          host: data.ingestHost ?? '',
          environment: data.environment ?? 'production',
          shop: data.shop,
          appVersion: data.appVersion ?? '1.1.0',
          replayEnabled: data.merchantReplayEnabled === true,
        });
      })
      .catch(() => {
        // An unreachable settings endpoint means no analytics. It must not mean
        // a broken admin.
      });
  }, [authenticatedFetch]);
}

/**
 * Record which admin page the merchant is looking at.
 *
 * The page ENUM, never a URL: the admin's own URL carries the App Bridge
 * `host` parameter and, on first load, an `id_token`. Neither has any business
 * in an analytics property, and reducing the URL would still leave us one
 * refactor away from sending one.
 *
 * @param page - The page now on screen
 */
export function useTrackAdminPage(page: PageName): void {
  const lastPage = useRef<PageName | null>(null);

  useEffect(() => {
    if (lastPage.current === page) return;
    lastPage.current = page;
    captureMerchantEvent('merchant_admin_opened', { page });
  }, [page]);
}
